import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import { sb } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import { fmtDateTime } from '@/lib/format';
import { resolveEtaScenarios } from '@/lib/etaScenarios';
import type { EtaAlert, EtaProfitableAlert, UserSettings } from '@/types/domain';

/** Форматирует числовое значение spread с одним знаком после запятой. */
function fmtSpread(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}%`;
}

/** Форматирует цену с нужным количеством знаков. */
function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function fmtSize(v: number | null | undefined, base?: string): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '';
  const n = v.toLocaleString('en-US', {
    minimumSignificantDigits: 1,
    maximumSignificantDigits: 4,
    useGrouping: false,
  });
  return base ? `${n} ${base}` : n;
}

function fmtNetUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}$${v.toFixed(2)}`;
}

function fmtApr(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(0)}%`;
}

function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
}

/** Извлекает базовый актив (BTC/ETH) из канонического ключа. */
function extractBase(normalizedKey: string): string {
  const parts = normalizedKey.split('-');
  const baseRaw = parts[0] ?? '';
  return (baseRaw.includes('_') ? baseRaw.split('_')[0] : baseRaw).toUpperCase();
}

/** Форматирует дату экспирации из ISO в читаемый вид. */
function fmtExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch {
    return iso;
  }
}

/** Раздел Eta. Мониторинг расхождений bid/ask по опционам между биржами. */
export function EtaPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const navigate = useNavigate();

  // Настройки из user_settings
  const [etaEnabled, setEtaEnabled] = useState(false);
  const [thresholdPct, setThresholdPct] = useState('20');
  const [horizonDays, setHorizonDays] = useState('30');
  const [minNetUsd, setMinNetUsd] = useState('0');
  const [minAprPct, setMinAprPct] = useState('0');
  const [onlyProfitable, setOnlyProfitable] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // История алертов
  const [alerts, setAlerts] = useState<EtaAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [profitableAlerts, setProfitableAlerts] = useState<EtaProfitableAlert[]>([]);
  const [profitableLoading, setProfitableLoading] = useState(true);
  const [profitablePage, setProfitablePage] = useState(0);
  const profitablePageSize = 50;
  const minNet = Number(minNetUsd);
  const minApr = Number(minAprPct);

  const filteredAlerts = alerts.filter((a) => {
    const { bestNet, bestApr } = resolveEtaScenarios(a);
    if (onlyProfitable && !(bestNet != null && bestNet > 0)) return false;
    if (Number.isFinite(minNet) && minNet > 0 && !(bestNet != null && bestNet >= minNet)) return false;
    if (Number.isFinite(minApr) && minApr > 0 && !(bestApr != null && bestApr >= minApr)) return false;
    return true;
  });

  // Загрузка настроек и истории
  useEffect(() => {
    if (!userId) return;

    void sb
      .from('user_settings')
      .select('feature_eta, eta_threshold_pct, eta_horizon_days, eta_min_net_usd, eta_min_apr_pct, eta_only_profitable')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        setSettingsLoading(false);
        if (error) {
          console.error('[EtaPage] load settings error:', error.message);
          setSettingsError(`Не удалось загрузить настройки: ${error.message}`);
          return;
        }
        const s = data as Pick<UserSettings, 'feature_eta' | 'eta_threshold_pct' | 'eta_horizon_days' | 'eta_min_net_usd' | 'eta_min_apr_pct' | 'eta_only_profitable'> | null;
        if (s) {
          setEtaEnabled(s.feature_eta ?? false);
          setThresholdPct(String(s.eta_threshold_pct ?? 20));
          setHorizonDays(String(s.eta_horizon_days ?? 30));
          setMinNetUsd(String(s.eta_min_net_usd ?? 0));
          setMinAprPct(String(s.eta_min_apr_pct ?? 0));
          setOnlyProfitable(s.eta_only_profitable ?? false);
        }
      });

    void sb
      .from('eta_alerts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100)
      .then(({ data, error }) => {
        setAlertsLoading(false);
        if (error) {
          console.error('[EtaPage] load alerts error:', error.message);
          return;
        }
        setAlerts((data ?? []) as EtaAlert[]);
      });

  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setProfitableLoading(true);
    void sb
      .from('eta_profitable_alerts')
      .select('*')
      .order('created_at', { ascending: false })
      .range(profitablePage * profitablePageSize, (profitablePage + 1) * profitablePageSize - 1)
      .then(({ data, error }) => {
        setProfitableLoading(false);
        if (error) {
          console.error('[EtaPage] load profitable alerts error:', error.message);
          return;
        }
        setProfitableAlerts((data ?? []) as EtaProfitableAlert[]);
      });
  }, [userId, profitablePage]);

  // Realtime-подписка на новые алерты
  useEffect(() => {
    const ch = sb
      .channel('eta-alerts-rt')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'eta_alerts' },
        (payload) => {
          setAlerts((prev) => [payload.new as EtaAlert, ...prev].slice(0, 100));
        },
      )
      .subscribe();
    return () => {
      void sb.removeChannel(ch);
    };
  }, []);

  const saveSettings = useCallback(async (
    enabled: boolean,
    threshold: string,
    horizon: string,
    netUsd: string,
    aprPct: string,
    onlyProfit: boolean,
  ): Promise<boolean> => {
    if (!userId) {
      setSettingsError('Не авторизован. Откройте приложение через Telegram.');
      notify('error');
      return false;
    }
    setSaving(true);
    setSettingsError(null);
    const thr = Number(threshold);
    const hor = Number(horizon);
    const net = Number(netUsd);
    const apr = Number(aprPct);
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        feature_eta: enabled,
        eta_threshold_pct: Number.isFinite(thr) && thr > 0 ? thr : 20,
        eta_horizon_days: Number.isFinite(hor) && hor > 0 ? Math.round(hor) : 30,
        eta_min_net_usd: Number.isFinite(net) && net >= 0 ? net : 0,
        eta_min_apr_pct: Number.isFinite(apr) && apr >= 0 ? apr : 0,
        eta_only_profitable: onlyProfit,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    setSaving(false);
    if (error) {
      console.error('[EtaPage] save settings error:', error.message);
      setSettingsError(error.message);
      notify('error');
      return false;
    }
    notify('success');
    return true;
  }, [userId, notify]);

  const toggleEnabled = async (): Promise<void> => {
    const prev = etaEnabled;
    const next = !prev;
    setEtaEnabled(next);
    const ok = await saveSettings(next, thresholdPct, horizonDays, minNetUsd, minAprPct, onlyProfitable);
    if (!ok) setEtaEnabled(prev);
  };

  return (
    <Page title="Eta — Межбиржевой спред">
      <Section
        header="О функции"
        footer="После каждого скрапа опционов (раз в 30 минут) сравниваются bid/ask одного и того же опциона между всеми биржами. Если разница превышает порог — приходит уведомление."
      >
        <div style={{ padding: 'var(--space-md) var(--space-lg)', color: 'var(--tg-text)', fontSize: 'var(--font-md)' }}>
          ⚖️ Межбиржевой мониторинг спреда: Gate, Binance, Bybit, Delta, Deribit, OKX и другие.
        </div>
      </Section>

      <Section header="Настройки мониторинга">
        {settingsLoading ? (
          <Spinner />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-md) var(--space-lg)',
              }}
            >
              <span style={{ color: 'var(--tg-text)', fontSize: 'var(--font-md)' }}>Включить мониторинг</span>
              <Button
                variant={etaEnabled ? 'secondary' : 'primary'}
                disabled={saving}
                onClick={() => void toggleEnabled()}
                style={{ minWidth: 110 }}
              >
                {saving ? '…' : etaEnabled ? 'Выключить' : 'Включить'}
              </Button>
            </div>
            <Input
              label="Порог спреда (%)"
              type="number"
              value={thresholdPct}
              onChange={(e) => setThresholdPct(e.target.value)}
              onBlur={() => void saveSettings(etaEnabled, thresholdPct, horizonDays, minNetUsd, minAprPct, onlyProfitable)}
            />
            <Input
              label="Горизонт экспирации (дней)"
              type="number"
              value={horizonDays}
              onChange={(e) => setHorizonDays(e.target.value)}
              onBlur={() => void saveSettings(etaEnabled, thresholdPct, horizonDays, minNetUsd, minAprPct, onlyProfitable)}
            />
            <Input
              label="Min net profit (USD)"
              type="number"
              value={minNetUsd}
              onChange={(e) => setMinNetUsd(e.target.value)}
              onBlur={() => void saveSettings(etaEnabled, thresholdPct, horizonDays, minNetUsd, minAprPct, onlyProfitable)}
            />
            <Input
              label="Min APR % (со шортом)"
              type="number"
              value={minAprPct}
              onChange={(e) => setMinAprPct(e.target.value)}
              onBlur={() => void saveSettings(etaEnabled, thresholdPct, horizonDays, minNetUsd, minAprPct, onlyProfitable)}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-md) var(--space-lg)',
              }}
            >
              <span style={{ color: 'var(--tg-text)', fontSize: 'var(--font-md)' }}>Только прибыльные (net {'>'} 0)</span>
              <input
                type="checkbox"
                checked={onlyProfitable}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setOnlyProfitable(checked);
                  void saveSettings(etaEnabled, thresholdPct, horizonDays, minNetUsd, minAprPct, checked).then((ok) => {
                    if (!ok) setOnlyProfitable(!checked);
                  });
                }}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
            </div>
            {settingsError && (
              <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
                Ошибка: {settingsError}
              </div>
            )}
          </>
        )}
      </Section>

      <Section
        header="История алертов"
        footer="Обновляется в реальном времени. Показаны последние 100 алертов."
      >
        {alertsLoading ? (
          <Spinner />
        ) : filteredAlerts.length === 0 ? (
          <Empty
            icon="⚖️"
            title={alerts.length === 0 ? 'Алертов пока нет' : 'Нет алертов под текущие фильтры'}
            hint={alerts.length === 0
              ? (etaEnabled ? 'Следующая проверка — при ближайшем скрапе (раз в 30 минут).' : 'Включите мониторинг, чтобы получать уведомления о расхождениях цен.')
              : 'Ослабьте фильтры Min net / Min APR или выключите "Только прибыльные".'}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
                color: 'var(--tg-text)',
              }}
            >
              <thead>
                <tr style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                  {[
                    'Время',
                    'Опцион',
                    'Тип',
                    'Страйк',
                    'Экспирация',
                    'Min Bid (биржа)',
                    'Max Bid (биржа)',
                    'Bid %',
                     'Min Ask (биржа)',
                     'Max Ask (биржа)',
                      'Ask %',
                      'Arb %',
                      'Net (USD)',
                      'Required Capital',
                      'APR %',
                      'Сценарий',
                    ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '6px 8px',
                        textAlign: 'left',
                        color: 'var(--tg-hint)',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((a) => {
                  const base = extractBase(a.normalized_key);
                  const { bestNet, bestApr, bestScenario, noShort, withShort } = resolveEtaScenarios(a);
                  const bestCapital = bestScenario === 'Со шортом'
                    ? withShort?.capitalRequiredUsd ?? withShort?.totalCapitalUsd ?? null
                    : bestScenario === 'Без шорта'
                      ? noShort?.capitalRequiredUsd ?? null
                      : null;
                  // Use base-normalized sizes when available
                  const minBidSz = a.min_bid_size_base ?? a.min_bid_size;
                  const maxBidSz = a.max_bid_size_base ?? a.max_bid_size;
                  const minAskSz = a.min_ask_size_base ?? a.min_ask_size;
                  const maxAskSz = a.max_ask_size_base ?? a.max_ask_size;
                  return (
                    <tr
                      key={a.id}
                      style={{ borderBottom: '0.5px solid var(--tg-separator)', cursor: 'pointer' }}
                      onClick={() => navigate(`/eta/${a.id}`)}
                    >
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--tg-hint)', fontSize: 11 }}>
                        {fmtDateTime(a.created_at)}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>
                        {a.normalized_key}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            color: a.option_type === 'C' ? '#34c759' : '#ff3b30',
                          }}
                        >
                          {a.option_type}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {fmtPrice(a.strike)}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {fmtExpiry(a.expiry)}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {fmtPrice(a.min_bid)}
                        {a.min_bid_exchange && (
                          <span style={{ color: 'var(--tg-hint)', fontSize: 10, marginLeft: 3 }}>
                            ({a.min_bid_exchange})
                            {minBidSz != null && minBidSz > 0 && ` ×${fmtSize(minBidSz, base)}`}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {fmtPrice(a.max_bid)}
                        {a.max_bid_exchange && (
                          <span style={{ color: 'var(--tg-hint)', fontSize: 10, marginLeft: 3 }}>
                            ({a.max_bid_exchange})
                            {maxBidSz != null && maxBidSz > 0 && ` ×${fmtSize(maxBidSz, base)}`}
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          color: (a.bid_spread_pct ?? 0) >= 20 ? '#ff9500' : 'var(--tg-text)',
                          fontWeight: (a.bid_spread_pct ?? 0) >= 20 ? 700 : 400,
                        }}
                      >
                        {fmtSpread(a.bid_spread_pct)}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {fmtPrice(a.min_ask)}
                        {a.min_ask_exchange && (
                          <span style={{ color: 'var(--tg-hint)', fontSize: 10, marginLeft: 3 }}>
                            ({a.min_ask_exchange})
                            {minAskSz != null && minAskSz > 0 && ` ×${fmtSize(minAskSz, base)}`}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {fmtPrice(a.max_ask)}
                        {a.max_ask_exchange && (
                          <span style={{ color: 'var(--tg-hint)', fontSize: 10, marginLeft: 3 }}>
                            ({a.max_ask_exchange})
                            {maxAskSz != null && maxAskSz > 0 && ` ×${fmtSize(maxAskSz, base)}`}
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          color: (a.ask_spread_pct ?? 0) >= 20 ? '#ff9500' : 'var(--tg-text)',
                          fontWeight: (a.ask_spread_pct ?? 0) >= 20 ? 700 : 400,
                        }}
                      >
                        {fmtSpread(a.ask_spread_pct)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          color: (a.cross_spread_pct ?? 0) > 0 ? '#34c759' : 'var(--tg-hint)',
                          fontWeight: (a.cross_spread_pct ?? 0) > 0 ? 700 : 400,
                        }}
                        title="Межбиржевой арбитраж: (лучший bid на одной бирже − лучший ask на другой) / ask"
                      >
                        {fmtSpread(a.cross_spread_pct ?? null)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          color: bestNet == null ? 'var(--tg-hint)' : bestNet > 0 ? '#34c759' : '#ff3b30',
                          fontWeight: bestNet != null ? 700 : 400,
                        }}
                      >
                        {fmtNetUsd(bestNet)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {fmtUsd(bestCapital)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          color: bestApr == null ? 'var(--tg-hint)' : '#ff9500',
                        }}
                      >
                        {fmtApr(bestApr)}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--tg-hint)' }}>
                        {bestScenario}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <Section
        header="Прибыльные алерты (Net > 0) - полная история"
        footer="Сохраняются все алерты с положительным Net. Без лимита по количеству."
      >
        {profitableLoading ? (
          <Spinner />
        ) : profitableAlerts.length === 0 ? (
          <Empty icon="📈" title="Пока нет прибыльных алертов" hint="Записи появятся автоматически при Net > 0." />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  color: 'var(--tg-text)',
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                    {[
                      'Время',
                      'Опцион',
                      'Тип',
                      'Страйк',
                      'Экспирация',
                      'Min Bid',
                      'Max Bid',
                      'Bid %',
                      'Min Ask',
                      'Max Ask',
                      'Ask %',
                      'Net (USD)',
                      'Required Capital',
                      'APR %',
                      'Сценарий',
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '6px 8px',
                          textAlign: 'left',
                          color: 'var(--tg-hint)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {profitableAlerts.map((entry) => {
                    const a = entry.alert;
                    const base = extractBase(a.normalized_key);
                    const minBidSz = a.min_bid_size_base ?? a.min_bid_size;
                    const maxBidSz = a.max_bid_size_base ?? a.max_bid_size;
                    const minAskSz = a.min_ask_size_base ?? a.min_ask_size;
                    const maxAskSz = a.max_ask_size_base ?? a.max_ask_size;
                    return (
                      <tr
                        key={entry.id}
                        style={{ borderBottom: '0.5px solid var(--tg-separator)', cursor: 'pointer' }}
                        onClick={() => navigate(`/eta/${entry.eta_alert_id ?? a.id}`)}
                      >
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--tg-hint)', fontSize: 11 }}>
                          {fmtDateTime(entry.created_at)}
                        </td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>
                          {a.normalized_key}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{a.option_type}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtPrice(a.strike)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtExpiry(a.expiry)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {fmtPrice(a.min_bid)}{a.min_bid_exchange ? ` (${a.min_bid_exchange})` : ''}{minBidSz ? ` ×${fmtSize(minBidSz, base)}` : ''}
                        </td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {fmtPrice(a.max_bid)}{a.max_bid_exchange ? ` (${a.max_bid_exchange})` : ''}{maxBidSz ? ` ×${fmtSize(maxBidSz, base)}` : ''}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtSpread(a.bid_spread_pct)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {fmtPrice(a.min_ask)}{a.min_ask_exchange ? ` (${a.min_ask_exchange})` : ''}{minAskSz ? ` ×${fmtSize(minAskSz, base)}` : ''}
                        </td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {fmtPrice(a.max_ask)}{a.max_ask_exchange ? ` (${a.max_ask_exchange})` : ''}{maxAskSz ? ` ×${fmtSize(maxAskSz, base)}` : ''}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtSpread(a.ask_spread_pct)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#34c759', fontWeight: 700 }}>{fmtNetUsd(entry.net_usd)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtUsd(entry.required_capital_usd)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#ff9500' }}>{fmtApr(entry.apr_pct)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--tg-hint)' }}>{entry.scenario}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '10px var(--space-lg)' }}>
              <Button variant="secondary" disabled={profitablePage === 0} onClick={() => setProfitablePage((p) => Math.max(0, p - 1))}>
                ← Назад
              </Button>
              <Button variant="secondary" disabled={profitableAlerts.length < profitablePageSize} onClick={() => setProfitablePage((p) => p + 1)}>
                Вперёд →
              </Button>
            </div>
          </>
        )}
      </Section>
    </Page>
  );
}
